import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { track } from '@amplitude/analytics-browser'
import { colors } from 'src/components/app-ui'
import { AssignmentFieldNames } from 'src/model/assignment'
import { languages } from 'src/model/languages'
import { shopLanguageGetService } from 'src/api/shop'
import {
  createCourseAction,
  courseGetSuccess,
} from 'src/reducers/course/action'
import { getCardSuccess } from 'src/reducers/card/action'
import useStyles from './styles'

/**
 * Create from scratch: course name + interface language, then opens the new
 * course in the editor. Same create call as the old naming modal.
 */
const CreateCourseModal = ({ open, pathId, pathName, onBack, onClose }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const inputRef = useRef(null)
  const [name, setName] = useState('')
  const [countryCode, setCountryCode] = useState('en')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (!open) return
    setName('')
    setCountryCode('en')
    setCreating(false)
    const timer = setTimeout(() => inputRef.current?.focus(), 50)
    // New courses start in the workspace language.
    let cancelled = false
    shopLanguageGetService()
      .then(response => {
        const code = response?.data?.languageCode
        if (!cancelled && languages.some(item => item.countryCode === code)) {
          setCountryCode(code)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open])

  if (!open) return null

  const canCreate = name.trim().length > 0 && !creating

  const handleCreate = () => {
    if (!canCreate) return
    setCreating(true)
    dispatch(
      createCourseAction(
        {
          [AssignmentFieldNames.NAME]: name.trim(),
          CourseCollectionId: pathId || null,
          CountryCode: countryCode,
        },
        entity => {
          track('Course Created', { courseId: entity.id })
          onClose()
          dispatch(getCardSuccess(null))
          dispatch(courseGetSuccess(null))
          history.push(`/mini-course/${entity.id}/page`)
        },
      ),
    )
    // The create saga skips the callback on failure (it shows its own error).
    setTimeout(() => setCreating(false), 15000)
  }

  return createPortal(
    <div className={classes.backdrop} onClick={onClose}>
      <div
        className={classes.dialog}
        role="dialog"
        aria-modal="true"
        onClick={event => event.stopPropagation()}
      >
        <div className={classes.dialogHead}>
          <div>
            <div className={classes.dialogTitle}>Create course</div>
            {pathName && (
              <div className={classes.dialogSub}>
                It is added to {pathName}.
              </div>
            )}
          </div>
          <button
            type="button"
            title="Close"
            className={classes.close}
            onClick={onClose}
          >
            <Icon icon="tabler:x" width={18} height={18} />
          </button>
        </div>

        <label className={classes.field}>
          Course name
          <input
            ref={inputRef}
            className={classes.input}
            value={name}
            placeholder="e.g. Expense policy basics"
            onChange={event => setName(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && handleCreate()}
          />
        </label>

        <div className={classes.field}>
          Interface language
          <div className={classes.selectWrap}>
            <select
              className={`${classes.input} ${classes.select}`}
              value={countryCode}
              onChange={event => setCountryCode(event.target.value)}
            >
              {languages.map(language => (
                <option key={language.countryCode} value={language.countryCode}>
                  {language.name}
                </option>
              ))}
            </select>
            <span className={classes.selectChevron}>
              <Icon icon="tabler:chevron-down" width={16} height={16} />
            </span>
          </div>
          <span className={classes.hint}>
            Language of buttons and system texts like Next and Quiz.
          </span>
        </div>

        <div className={classes.actions}>
          <button type="button" className={classes.textButton} onClick={onBack}>
            Back
          </button>
          <button
            type="button"
            className={classes.primaryButton}
            style={{
              background: canCreate ? colors.primary : '#99BFFD',
              cursor: canCreate ? 'pointer' : 'default',
            }}
            disabled={!canCreate}
            onClick={handleCreate}
          >
            {creating ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default CreateCourseModal
